/*************************************************************************
 * ChartComponent.jsx
 * Regenerated to accept a company URL parameter and route all endpoints to
 *   https://{company}.ogpumper.net  (or ogendeavors.com for "src")
 *
 * Added (per request):
 * - Pull pumper/user list from:   /api/userdetails.php
 * - Add "Pumper" dropdown that drives PumpersCsv=... in prod requests
 * - When PumpersCsv is set, production data is pulled via Rpt=EM (known to
 *   support PumpersCsv) and aggregated client-side by day/month so the chart
 *   still works for ~ALL~ leases.
 *
 * Layout safeguard:
 * - Lease/Well "nav" arrow buttons are kept INLINE with their dropdowns
 *   via .select-with-nav so they do not drop to the next line.
 *
 * Updates (12/2025 requests):
 * - Allow user to turn on/off series from Settings (series visibility toggles)
 * - Show user name in Settings title (Settings (lori)) using URL Name/name
 * - Don't include "Reset Legend" button on print
 * - Don't include inactive pumpers in the Pumper filter dropdown
 * - If user changes From/To dates, Time Range becomes "Custom"
 * - Fix print date off-by-one (YYYY-MM-DD parsing in local time)
 *
 * UPDATE (2026-03-05):
 * - Production API now returns WellsOn / WellsDown / TotalWells
 * - Current PRODUCTION chart now also supports and renders:
 *     Wells On, Wells Down, Total Wells
 * - Well charts still support the same three wells series
 * - WellsDown = TotalWells - WellsOn fallback when not returned
 *
 * UPDATE (2026-03-09):
 * - Bars now render behind lines for cleaner z-ordering in ComposedChart
 * - Lines have stronger visibility via higher strokeWidth + activeDot
 * - Bars use fillOpacity for better line readability
 * - Color picker no longer closes during hex/RGB/slider edits
 * - SketchPicker now uses onChange for live updates
 * - Choosing a Tag in production auto-resets Lease to ~ALL~ when needed
 *
 * UPDATE (2026-03-16):
 * - Manual trendline is now rendered from explicit entered points
 *   (start date/value -> end date/value)
 * - Trendline no longer depends on API rows existing exactly on those dates
 * - Synthetic anchor points are injected only for the trendline when needed
 * - Trendline supports future projection as long as the visible From/To
 *   range includes the projected dates
 * - Trendline remains production-only and uses the left axis
 *************************************************************************/

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { SketchPicker } from "react-color";
import debounce from "lodash/debounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRedo,
  faCog,
  faPrint,
  faChartBar,
  faChartLine,
  faTimes,
  faExchangeAlt,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

/* -------------------------------------------------------------
   URL-parameter helpers  (Name is sticky; never overwritten)
-------------------------------------------------------------- */

/* Read the ORIGINAL search once (so later replaceState calls can't drop Name) */
const INITIAL_QS = new URLSearchParams(window.location.search);

/* Track which key was used ("Name" or "name") and keep its exact value */
const stickyNameKey = INITIAL_QS.has("Name")
  ? "Name"
  : INITIAL_QS.has("name")
    ? "name"
    : null;

const urlName = stickyNameKey ? INITIAL_QS.get(stickyNameKey) : null; // keep as-is
const urlRpt = INITIAL_QS.get("Rpt") || INITIAL_QS.get("rpt") || "CD";

const normalizeQD = (v) => {
  if (!v) return "30";
  const s = String(v).trim();
  if (!s) return "30";
  return s.toLowerCase() === "custom" ? "Custom" : s;
};

const rawQD = INITIAL_QS.get("QD") || INITIAL_QS.get("qd") || "30";
const urlQD = normalizeQD(rawQD);

const urlLeaseID =
  INITIAL_QS.get("LeaseID") || INITIAL_QS.get("leaseid") || "~ALL~";
const urlFrom =
  INITIAL_QS.get("From") ||
  INITIAL_QS.get("FromDate") ||
  INITIAL_QS.get("from") ||
  INITIAL_QS.get("fromdate");
const urlThru =
  INITIAL_QS.get("Thru") ||
  INITIAL_QS.get("ThruDate") ||
  INITIAL_QS.get("thru") ||
  INITIAL_QS.get("thrudate");
const urlTag = INITIAL_QS.get("Tag") || INITIAL_QS.get("tag") || "All";
const urlUserID =
  INITIAL_QS.get("UserID") ||
  INITIAL_QS.get("userID") ||
  INITIAL_QS.get("userid");
const urlToken = INITIAL_QS.get("token");
const urlGrant = INITIAL_QS.get("grant") || INITIAL_QS.get("Grant"); // optional, preserved

/* NEW: PumpersCsv (pumper filter) */
const urlPumpersCsv =
  INITIAL_QS.get("PumpersCsv") ||
  INITIAL_QS.get("pumperscsv") ||
  INITIAL_QS.get("PUMPERCSV") ||
  "";

/* -------------------------------------------------------------
   Company detection → API base: https://{company}.ogpumper.net
-------------------------------------------------------------- */
function sanitizeCompany(raw) {
  if (!raw) return "";
  const v = String(raw).trim().toLowerCase();
  return /^[a-z0-9-]+$/.test(v) ? v : "";
}

function detectCompany() {
  // 1) From query (read from ORIGINAL QS so it doesn't change under us)
  const qCompany =
    INITIAL_QS.get("Company") ||
    INITIAL_QS.get("company") ||
    INITIAL_QS.get("Subdomain") ||
    INITIAL_QS.get("subdomain") ||
    INITIAL_QS.get("c");
  if (qCompany) {
    const s = sanitizeCompany(qCompany);
    if (s) return s;
  }

  // 2) From path: /companyHome/:slug(/…)
  const parts = window.location.pathname.replace(/^\/+|\/+$/g, "").split("/");
  const idx = parts.indexOf("companyHome");
  if (idx !== -1 && parts[idx + 1]) {
    const s = sanitizeCompany(parts[idx + 1]);
    if (s) return s;
  }

  // 3) From host subdomain: foo.example.com → foo
  const hostParts = window.location.hostname.split(".");
  if (hostParts.length > 2) {
    const s = sanitizeCompany(hostParts[0]);
    if (s) return s;
  }

  // 4) Fallback default
  return "stasney";
}

const COMPANY = detectCompany();

/* Use ogendeavors.com for "src", otherwise ogpumper.net */
const API_BASE =
  COMPANY === "src"
    ? `https://${COMPANY}.ogendeavors.com`
    : `https://${COMPANY}.ogpumper.net`;

/* -------------------------------------------------------------
   Derive From/Thru dates from QD (mirrors original quick-link logic)
-------------------------------------------------------------- */
const deriveDates = (qd) => {
  const today = moment();
  switch (qd) {
    case "CM":
      return {
        from: today.clone().startOf("month").format("YYYY-MM-DD"),
        thru: today.clone().endOf("month").format("YYYY-MM-DD"),
      };
    case "3D":
      return {
        from: today.clone().subtract(2, "days").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "7D":
      return {
        from: today.clone().subtract(6, "days").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "LM":
      return {
        from: today
          .clone()
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD"),
        thru: today
          .clone()
          .subtract(1, "month")
          .endOf("month")
          .format("YYYY-MM-DD"),
      };
    case "3M":
      return {
        from: today
          .clone()
          .subtract(3, "months")
          .add(1, "day")
          .format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "6M":
      return {
        from: today
          .clone()
          .subtract(6, "months")
          .add(1, "day")
          .format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "CY":
      return {
        from: today.clone().startOf("year").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "LY":
      return {
        from: today
          .clone()
          .subtract(1, "year")
          .startOf("year")
          .format("YYYY-MM-DD"),
        thru: today
          .clone()
          .subtract(1, "year")
          .endOf("year")
          .format("YYYY-MM-DD"),
      };
    case "30":
    default:
      return {
        from: today.clone().subtract(29, "days").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
  }
};

/* -------------------------------------------------------------
   Initial From/Thru (URL overrides derived defaults)
-------------------------------------------------------------- */
const qdForDefaults = urlQD === "Custom" ? "30" : urlQD;
const dateDefaults = deriveDates(qdForDefaults);
const initialFrom = urlFrom || dateDefaults.from;
const initialThru = urlThru || dateDefaults.thru;

const initialQuickLink =
  urlQD === "Custom"
    ? "Custom"
    : (urlFrom || urlThru) &&
        ((urlFrom && urlFrom !== dateDefaults.from) ||
          (urlThru && urlThru !== dateDefaults.thru))
      ? "Custom"
      : urlQD;

/* -------------------------------------------------------------
   Helper: remove <=0 values when in log mode
-------------------------------------------------------------- */
const filterLogData = (data) =>
  data.map((d) => ({
    ...d,
    Oil: d.Oil > 0 ? d.Oil : d.Oil === 0 ? 0 : null,
    ProducedWater:
      d.ProducedWater > 0 ? d.ProducedWater : d.ProducedWater === 0 ? 0 : null,
    InjectedWater:
      d.InjectedWater > 0 ? d.InjectedWater : d.InjectedWater === 0 ? 0 : null,
    Gas: d.Gas > 0 ? d.Gas : d.Gas === 0 ? 0 : null,
    Tbg: d.Tbg > 0 ? d.Tbg : d.Tbg === 0 ? 0 : null,
    Csg: d.Csg > 0 ? d.Csg : d.Csg === 0 ? 0 : null,
    TestOil: d.TestOil > 0 ? d.TestOil : d.TestOil === 0 ? 0 : null,
    TestWater: d.TestWater > 0 ? d.TestWater : d.TestWater === 0 ? 0 : null,
    TestGas: d.TestGas > 0 ? d.TestGas : d.TestGas === 0 ? 0 : null,
    JTF: d.JTF > 0 ? d.JTF : d.JTF === 0 ? 0 : null,
    FTF: d.FTF > 0 ? d.FTF : d.FTF === 0 ? 0 : null,
    FAP: d.FAP > 0 ? d.FAP : d.FAP === 0 ? 0 : null,
    GFFAP: d.GFFAP > 0 ? d.GFFAP : d.GFFAP === 0 ? 0 : null,
    SND: d.SND > 0 ? d.SND : d.SND === 0 ? 0 : null,
    WellsOn: d.WellsOn > 0 ? d.WellsOn : d.WellsOn === 0 ? 0 : null,
    WellsDown: d.WellsDown > 0 ? d.WellsDown : d.WellsDown === 0 ? 0 : null,
    TotalWells: d.TotalWells > 0 ? d.TotalWells : d.TotalWells === 0 ? 0 : null,
  }));

/* -------------------------------------------------------------
   Helper: convert a GaugeDate key to a Unix-ms timestamp for the
   time-proportional x-axis.  Daily keys are "YYYY-MM-DD", monthly
   keys are "MM-YYYY".  Uses strict parsing to avoid moment
   falling back to native Date (which would cause UTC off-by-one).
-------------------------------------------------------------- */
const gaugeKeyToTs = (gaugeKey, reportType) => {
  if (!gaugeKey) return 0;
  const m =
    reportType === "CM"
      ? moment(gaugeKey, "MM-YYYY", true)
      : moment(gaugeKey, "YYYY-MM-DD", true);
  return m.isValid() ? m.valueOf() : 0;
};

/* Convert a YYYY-MM-DD date string (from the From/Thru picker) to
   a timestamp appropriate for the current report granularity.
   Monthly reports snap to start-of-month so the domain aligns with
   data points which also use start-of-month timestamps. */
const dateStrToTs = (dateStr, reportType) => {
  const m = moment(dateStr, "YYYY-MM-DD", true);
  if (!m.isValid()) return 0;
  return reportType === "CM" ? m.startOf("month").valueOf() : m.valueOf();
};

/* -------------------------------------------------------------
   Helper: pad data array so the x-axis respects fromDate/thruDate
   even when the API returns fewer rows than the selected range.
   Injects a ghost row (all numeric values undefined) at the start
   and/or end so the axis domain includes the user's selected range
   even when API returns fewer rows.
-------------------------------------------------------------- */
const EMPTY_ROW_KEYS = [
  "Oil",
  "ProducedWater",
  "InjectedWater",
  "Gas",
  "Tbg",
  "Csg",
  "TestOil",
  "TestWater",
  "TestGas",
  "JTF",
  "FTF",
  "FAP",
  "GFFAP",
  "SND",
  "WellsOn",
  "WellsDown",
  "TotalWells",
];

const makeEmptyRow = (gaugeKey, ts) =>
  EMPTY_ROW_KEYS.reduce(
    (acc, k) => {
      acc[k] = undefined;
      return acc;
    },
    { GaugeDate: gaugeKey, __ghost: true, __ts: ts ?? 0 },
  );

const padDataToDateRange = (data, fromDate, thruDate, reportType) => {
  if (!fromDate || !thruDate) return data;
  const isMonthly = reportType === "CM";
  const fmt = isMonthly ? "MM-YYYY" : "YYYY-MM-DD";
  const granularity = isMonthly ? "month" : "day";
  const parse = (k) => moment(k, fmt, true);
  const startKey = isMonthly
    ? moment(fromDate, "YYYY-MM-DD").startOf("month").format(fmt)
    : fromDate;
  const endKey = isMonthly
    ? moment(thruDate, "YYYY-MM-DD").startOf("month").format(fmt)
    : thruDate;

  /* Pre-compute boundary timestamps for ghost rows so the time axis
     places them at their true calendar positions. */
  const startTs = gaugeKeyToTs(startKey, reportType);
  const endTs = gaugeKeyToTs(endKey, reportType);

  /* If API returned zero rows, still produce boundary ghost rows so the
     x-axis renders the user's selected date range (empty chart, not blank). */
  if (!data.length) {
    if (startKey === endKey) return [makeEmptyRow(startKey, startTs)];
    return [makeEmptyRow(startKey, startTs), makeEmptyRow(endKey, endTs)];
  }

  const result = [...data];
  if (parse(result[0].GaugeDate).isAfter(parse(startKey), granularity))
    result.unshift(makeEmptyRow(startKey, startTs));
  if (
    parse(result[result.length - 1].GaugeDate).isBefore(
      parse(endKey),
      granularity,
    )
  )
    result.push(makeEmptyRow(endKey, endTs));
  return result;
};

/* -------------------------------------------------------------
   Helper: generate explicit log-scale tick positions covering
   both major decades (1, 10, 100 …) and minor steps (2–9 within
   each decade).  The tickFormatter will suppress labels on minor
   ticks so the axis stays readable while CartesianGrid draws
   fine horizontal rules at every position.
-------------------------------------------------------------- */
const computeLogTicks = (minVal, maxVal) => {
  if (!minVal || !maxVal || minVal <= 0 || maxVal <= 0) return undefined;
  const lo = Math.floor(Math.log10(minVal));
  const hi = Math.ceil(Math.log10(maxVal));
  const ticks = [];
  for (let exp = lo; exp <= hi; exp++) {
    for (const m of [1, 2, 3, 4, 5, 6, 7, 8, 9]) {
      const v = m * Math.pow(10, exp);
      if (v >= minVal * 0.5 && v <= maxVal * 2) ticks.push(v);
    }
  }
  return ticks.length ? ticks : undefined;
};

/* -------------------------------------------------------------
   Trendline helpers
-------------------------------------------------------------- */
const isMonthlyChart = (chartView, reportType) =>
  chartView === "production" && reportType === "CM";

const getGaugeFormat = (chartView, reportType) =>
  isMonthlyChart(chartView, reportType) ? "MM-YYYY" : "YYYY-MM-DD";

const getGaugeGranularity = (chartView, reportType) =>
  isMonthlyChart(chartView, reportType) ? "month" : "day";

const parseGaugeMoment = (gaugeKey, chartView, reportType) =>
  moment(gaugeKey, getGaugeFormat(chartView, reportType), true);

const formatGaugeMoment = (gaugeMoment, chartView, reportType) =>
  gaugeMoment.format(getGaugeFormat(chartView, reportType));

const normalizeTrendInputMoment = (dateStr, chartView, reportType) => {
  const m = moment(dateStr, "YYYY-MM-DD", true);
  if (!m.isValid()) return m;
  return isMonthlyChart(chartView, reportType)
    ? m.startOf("month")
    : m.startOf("day");
};

const parseTrendNumber = (value) => {
  if (value === null || value === undefined) return NaN;
  const s = String(value).trim();
  if (!s) return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const compareGaugeKeys = (a, b, chartView, reportType) => {
  const am = parseGaugeMoment(a, chartView, reportType);
  const bm = parseGaugeMoment(b, chartView, reportType);

  if (am.isValid() && bm.isValid()) return am.valueOf() - bm.valueOf();
  if (am.isValid()) return -1;
  if (bm.isValid()) return 1;
  return String(a).localeCompare(String(b));
};

const makeTrendAnchorRow = (gaugeKey, ts) =>
  EMPTY_ROW_KEYS.reduce(
    (acc, k) => {
      acc[k] = undefined;
      return acc;
    },
    { GaugeDate: gaugeKey, __trendAnchor: true, __ts: ts ?? 0 },
  );

/* =============================================================
   MAIN COMPONENT
============================================================= */
const ChartComponent = () => {
  /* -----------------------------------------------------------
     User / URL-param initialisation
  ------------------------------------------------------------ */
  const userID = urlUserID;

  /* -----------------------------------------------------------
     State
  ------------------------------------------------------------ */
  const [reportType, setReportType] = useState(urlRpt);
  const [quickLink, setQuickLink] = useState(initialQuickLink);
  const [selectedLeaseID, setSelectedLeaseID] = useState(urlLeaseID);
  const [fromDate, setFromDate] = useState(initialFrom);
  const [thruDate, setThruDate] = useState(initialThru);
  const [selectedTag, setSelectedTag] = useState(urlTag);

  /* NEW: Pumper filter (drives PumpersCsv) */
  const [selectedPumperCsv, setSelectedPumperCsv] = useState(urlPumpersCsv);
  const [pumpers, setPumpers] = useState([]);
  const [pumpersLoaded, setPumpersLoaded] = useState(false);

  const [selectedWellID, setSelectedWellID] = useState("");
  const [wells, setWells] = useState([]);
  const [leases, setLeases] = useState([]);
  const [tags, setTags] = useState(["All"]);
  const [chartView, setChartView] = useState("production"); // production | wellTest

  const chartRef = useRef(null);
  /* Monotonic counter: incremented on every fetch, checked after await.
     Only the most recent fetch is allowed to write state, so a slow prior
     request can never overwrite a newer filter combination's results. */
  const fetchSeqRef = useRef(0);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  /* ---- chartTypes, colours, prefs, etc. ---------- */
  const [chartTypes, setChartTypes] = useState({
    Oil: "bar",
    ProducedWater: "bar",
    InjectedWater: "bar",
    Gas: "line",
    Tbg: "line",
    Csg: "line",
    TestOil: "bar",
    TestWater: "bar",
    TestGas: "line",
    JTF: "line",
    FTF: "line",
    FAP: "line",
    GFFAP: "line",
    SND: "line",
    WellsOn: "line",
    WellsDown: "line",
    TotalWells: "line",
  });

  const [colors, setColors] = useState({
    Oil: "#FF7F0E",
    ProducedWater: "#2CA02C",
    InjectedWater: "#1F77B4",
    Tbg: "#D62728",
    Csg: "#9467BD",
    Gas: "#8C564B",
    TestOil: "#FF7F0E",
    TestWater: "#2CA02C",
    TestGas: "#8C564B",
    JTF: "#FFBB78",
    FTF: "#98DF8A",
    FAP: "#AEC7E8",
    GFFAP: "#FF9896",
    SND: "#C5B0D5",
    WellsOn: "#0EA5E9",
    WellsDown: "#F97316",
    TotalWells: "#111827",
  });

  const [colorPicker, setColorPicker] = useState({
    visible: false,
    field: null,
  });
  const [logarithmic, setLogarithmic] = useState(false);
  const [trendline, setTrendline] = useState({
    enabled: false,
    startDate: "",
    startValue: "",
    endDate: "",
    endValue: "",
  });
  const [stacked, setStacked] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [disabledSeries, setDisabledSeries] = useState([]);

  const displayNameForSettings =
    (userID ? String(userID).trim() : "") ||
    (urlName && String(urlName).trim());

  const seriesKeys =
    chartView === "production"
      ? [
          "Oil",
          "ProducedWater",
          "InjectedWater",
          "Gas",
          "Tbg",
          "Csg",
          "WellsOn",
          "WellsDown",
          "TotalWells",
        ]
      : [
          "TestOil",
          "TestWater",
          "TestGas",
          "Tbg",
          "Csg",
          "JTF",
          "FTF",
          "FAP",
          "GFFAP",
          "SND",
          "WellsOn",
          "WellsDown",
          "TotalWells",
        ];

  const seriesConfig =
    chartView === "production"
      ? [
          { key: "Oil", name: "Oil", yAxisId: "left", stackId: "prod-left" },
          {
            key: "ProducedWater",
            name: "ProducedWater",
            yAxisId: "left",
            stackId: "prod-left",
          },
          {
            key: "InjectedWater",
            name: "InjectedWater",
            yAxisId: "left",
            stackId: "prod-left",
          },
          { key: "Gas", name: "Gas", yAxisId: "right", stackId: "prod-right" },
          { key: "Tbg", name: "Tbg", yAxisId: "right", stackId: "prod-right" },
          { key: "Csg", name: "Csg", yAxisId: "right", stackId: "prod-right" },
          {
            key: "WellsOn",
            name: "Wells On",
            yAxisId: "wells",
            stackId: undefined,
          },
          {
            key: "WellsDown",
            name: "Wells Down",
            yAxisId: "wells",
            stackId: undefined,
          },
          {
            key: "TotalWells",
            name: "Total Wells",
            yAxisId: "wells",
            stackId: undefined,
          },
        ]
      : [
          { key: "TestOil", name: "Test Oil", yAxisId: "left" },
          { key: "TestWater", name: "Test Water", yAxisId: "left" },
          { key: "TestGas", name: "Test Gas", yAxisId: "right" },
          { key: "Tbg", name: "Tbg", yAxisId: "right" },
          { key: "Csg", name: "Csg", yAxisId: "right" },
          { key: "JTF", name: "JTF", yAxisId: "right" },
          { key: "FTF", name: "FTF", yAxisId: "right" },
          { key: "FAP", name: "FAP", yAxisId: "right" },
          { key: "GFFAP", name: "GFFAP", yAxisId: "right" },
          { key: "SND", name: "SND", yAxisId: "right" },
          { key: "WellsOn", name: "Wells On", yAxisId: "wells" },
          { key: "WellsDown", name: "Wells Down", yAxisId: "wells" },
          { key: "TotalWells", name: "Total Wells", yAxisId: "wells" },
        ];

  const visibleSeriesConfig = seriesConfig.filter(
    (series) => !disabledSeries.includes(series.key),
  );
  const visibleBarSeries = visibleSeriesConfig.filter(
    (series) => chartTypes[series.key] === "bar",
  );
  const visibleLineSeries = visibleSeriesConfig.filter(
    (series) => chartTypes[series.key] === "line",
  );

  /* -----------------------------------------------------------
     Sync state → URL bar (preserve current company & params)
  ------------------------------------------------------------ */
  useEffect(() => {
    const url = new URL(window.location.href);
    const qs = url.searchParams;

    const set = (k, v) => {
      if (v === undefined || v === null || v === "") return;
      qs.set(k, String(v));
    };

    set("Rpt", reportType);
    set("QD", quickLink);
    set("LeaseID", selectedLeaseID);
    set("From", fromDate);
    set("Thru", thruDate);
    set("Tag", selectedTag);
    if (userID) set("UserID", userID);

    if (selectedPumperCsv) qs.set("PumpersCsv", selectedPumperCsv);
    else qs.delete("PumpersCsv");

    set("Company", COMPANY);

    if (urlToken) qs.set("token", urlToken);
    if (urlGrant) qs.set("grant", urlGrant);

    if (stickyNameKey && urlName && !qs.has(stickyNameKey)) {
      qs.set(stickyNameKey, urlName);
    }

    window.history.replaceState(
      null,
      "",
      `${url.pathname}?${qs.toString()}${url.hash}`,
    );
  }, [
    reportType,
    quickLink,
    selectedLeaseID,
    fromDate,
    thruDate,
    selectedTag,
    userID,
    selectedPumperCsv,
  ]);

  /* -----------------------------------------------------------
     Fetch user prefs
  ------------------------------------------------------------ */
  const fetchPreferences = useCallback(async () => {
    if (!userID) {
      console.warn(
        "fetchPreferences: missing userID; skipping ChartsPref fetch.",
      );
      return;
    }
    try {
      const url = `${API_BASE}/api/userdetails.php?id=${userID}&chartsPref=true`;
      const resp = await fetch(url, {
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      const json = await resp.json();
      if (json.success && json.ChartsPref) {
        const {
          chartTypes: prefChartTypes,
          colors: prefColors,
          logarithmic: prefLogarithmic,
          disabledSeries: prefDisabledSeries,
          trendline: prefTrendline,
          showTrendline: prefShowTrendline,
        } = json.ChartsPref;

        setChartTypes((prev) => ({ ...prev, ...(prefChartTypes || {}) }));
        setColors((prev) => ({ ...prev, ...(prefColors || {}) }));
        setLogarithmic(!!prefLogarithmic);
        setDisabledSeries(prefDisabledSeries || []);

        if (prefTrendline && typeof prefTrendline === "object") {
          setTrendline((prev) => ({
            ...prev,
            enabled: !!prefTrendline.enabled,
            startDate: prefTrendline.startDate || "",
            startValue:
              prefTrendline.startValue !== undefined &&
              prefTrendline.startValue !== null
                ? String(prefTrendline.startValue)
                : "",
            endDate: prefTrendline.endDate || "",
            endValue:
              prefTrendline.endValue !== undefined &&
              prefTrendline.endValue !== null
                ? String(prefTrendline.endValue)
                : "",
          }));
        } else if (prefShowTrendline !== undefined) {
          setTrendline((prev) => ({
            ...prev,
            enabled: !!prefShowTrendline,
          }));
        }
      }
    } catch (e) {
      console.error("fetchPreferences:", e);
    }
  }, [userID, urlToken]);

  /* -----------------------------------------------------------
     Fetch pumpers/users for PumpersCsv dropdown
  ------------------------------------------------------------ */
  const fetchPumpers = useCallback(async () => {
    setPumpersLoaded(false);

    const isDisabled = (v) => v === 1 || v === "1" || v === true;

    const norm = (s) => String(s ?? "").trim();
    const isBadUserId = (id) => {
      const v = norm(id).toLowerCase();
      return (
        v === "" ||
        v === "unassigned" ||
        v === "(unassigned)" ||
        v === "inactive" ||
        v === "disabled" ||
        v === "none" ||
        v === "null" ||
        v === "undefined"
      );
    };

    const isBadName = (name) => {
      const v = norm(name).toLowerCase();
      return (
        v === "unassigned" ||
        v === "(unassigned)" ||
        v === "inactive" ||
        v === "disabled"
      );
    };

    try {
      const resp = await fetch(`${API_BASE}/api/userdetails.php`, {
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      if (!resp.ok) throw new Error("Network response was not ok");
      const json = await resp.json();

      const users = Array.isArray(json?.users) ? json.users : [];
      const pumperUsers = users
        .filter((u) => u && !isBadUserId(u.UserID))
        .filter((u) => (u.Role || "").toUpperCase() === "P")
        .filter((u) => !isDisabled(u.Disabled))
        .filter((u) => !isBadName(u.FullName))
        .sort((a, b) => {
          const an = (a.FullName || a.UserID || "").toLowerCase();
          const bn = (b.FullName || b.UserID || "").toLowerCase();
          return an.localeCompare(bn);
        });

      setPumpers(pumperUsers);
    } catch (e) {
      console.error("fetchPumpers:", e);
      setPumpers([]);
    } finally {
      setPumpersLoaded(true);
    }
  }, [urlToken]);

  useEffect(() => {
    if (!pumpersLoaded) return;
    const val = String(selectedPumperCsv || "").trim();
    if (!val) return;
    const exists = pumpers.some((p) => String(p.UserID) === val);
    if (!exists) setSelectedPumperCsv("");
  }, [pumpersLoaded, pumpers, selectedPumperCsv]);

  /* -----------------------------------------------------------
     Fetch leases + tags
  ------------------------------------------------------------ */
  const fetchLeases = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/leases.php?tokdebug=1`, {
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      if (!resp.ok) throw new Error("Network response was not ok");
      const leaseData = await resp.json();

      setLeases(leaseData);
      setWells(
        leaseData.flatMap((lease) =>
          lease.Wells.map((w) => ({ ...w, LeaseName: lease.LeaseName })),
        ),
      );
      const uniqueTags = [
        ...new Set(
          leaseData.flatMap((l) =>
            [l.Tag1, l.Tag2, l.Tag3, l.Tag4].filter(Boolean),
          ),
        ),
      ];
      setTags(["All", ...uniqueTags]);
    } catch (e) {
      console.error("fetchLeases:", e);
    }
  }, [urlToken]);

  /* -----------------------------------------------------------
     Fetch production data
  ------------------------------------------------------------ */
  const fetchChartData = useCallback(async () => {
    try {
      const hasSpecificLease =
        selectedLeaseID && selectedLeaseID !== "~ALL~";

      const usePumperFilter =
        !hasSpecificLease &&
        selectedPumperCsv !== undefined &&
        selectedPumperCsv !== null &&
        String(selectedPumperCsv).trim() !== "";

      const rptForEndpoint = usePumperFilter ? "EM" : reportType;
      const qdForEndpoint = quickLink === "Custom" ? "30" : quickLink;

      const emPrimaryOnlyParam = usePumperFilter
        ? `&EMPumperScope=PUMPER_ONLY`
        : "";

      /* BUG-1 FIX: The EM query path (used when a Pumper is selected) reads
         TagsCsv — NOT the legacy Tag param — for tag-based lease filtering.
         Without sending TagsCsv, selecting Tag + Pumper would silently drop
         the tag filter, returning pumper data across ALL tags.
         We now send TagsCsv alongside the legacy Tag param whenever a non-"All"
         tag is active, ensuring the EM path applies the tag→lease constraint
         as an AND with the pumper constraint.  The legacy Tag param is kept
         for the CD/CM paths which still read it. */
      const tagParams =
        selectedTag && selectedTag !== "All"
          ? `&Tag=${encodeURIComponent(selectedTag)}` +
            `&TagsCsv=${encodeURIComponent(selectedTag)}`
          : `&Tag=${encodeURIComponent(selectedTag)}`;

      const url =
        `${API_BASE}/service_testprod.php?Rpt=${encodeURIComponent(
          rptForEndpoint,
        )}` +
        `&QD=${encodeURIComponent(qdForEndpoint)}` +
        `&LeaseID=${encodeURIComponent(selectedLeaseID)}` +
        `&From=${fromDate}` +
        `&Thru=${thruDate}` +
        tagParams +
        (usePumperFilter
          ? `&PumpersCsv=${encodeURIComponent(
              String(selectedPumperCsv).trim(),
            )}`
          : "") +
        emPrimaryOnlyParam;

      const resp = await fetch(url, {
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      if (!resp.ok) {
        throw new Error("Network response was not ok");
      }

      const json = await resp.json();
      const rows = Array.isArray(json) ? json : [];

      const toNum = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };

      const normalized = rows.map((i) => {
        const rawGauge = i.GaugeDate;

        const gaugeKey =
          reportType === "CM"
            ? i.MonthYear
              ? i.MonthYear
              : rawGauge
                ? moment(rawGauge).format("MM-YYYY")
                : ""
            : rawGauge
              ? moment(rawGauge).format("YYYY-MM-DD")
              : "";

        const wellsOn = toNum(i.WellsOn);
        const totalWells = toNum(i.TotalWells);
        const wellsDownRaw =
          i.WellsDown !== undefined &&
          i.WellsDown !== null &&
          i.WellsDown !== ""
            ? toNum(i.WellsDown)
            : Math.max(0, totalWells - wellsOn);

        return {
          GaugeDate: gaugeKey,
          /* __ts: Unix-ms timestamp for time-proportional x-axis */
          __ts: gaugeKeyToTs(gaugeKey, reportType),
          Oil: toNum(i.Produced),
          ProducedWater: toNum(i.ProducedWaterTotal),
          InjectedWater: toNum(i.InjectedWaterTotal),
          Gas: toNum(i.Gas),
          Tbg: toNum(i.Tbg ?? i.tbg),
          Csg: toNum(i.Csg ?? i.csg),
          WellsOn: wellsOn,
          WellsDown: wellsDownRaw,
          TotalWells: totalWells,
        };
      });

      const byKey = new Map();
      for (const r of normalized) {
        if (!r.GaugeDate) continue;
        const prev = byKey.get(r.GaugeDate);
        if (!prev) {
          byKey.set(r.GaugeDate, { ...r }); // __ts comes along via spread
        } else {
          prev.Oil += r.Oil;
          prev.ProducedWater += r.ProducedWater;
          prev.InjectedWater += r.InjectedWater;
          prev.Gas += r.Gas;
          prev.Tbg += r.Tbg;
          prev.Csg += r.Csg;

          /* Production well params:
             - sum daily rows when multiple leases roll up into same date bucket
             - if monthly rows already come pre-aggregated, this still behaves correctly
          */
          prev.WellsOn += r.WellsOn;
          prev.WellsDown += r.WellsDown;
          prev.TotalWells += r.TotalWells;
        }
      }

      const result = Array.from(byKey.values());

      const keyToTime = (k) =>
        reportType === "CM"
          ? moment(k, "MM-YYYY", true).valueOf()
          : moment(k, "YYYY-MM-DD", true).valueOf();

      result.sort((a, b) => keyToTime(a.GaugeDate) - keyToTime(b.GaugeDate));

      return result;
    } catch (e) {
      console.error("fetchChartData:", e);
      return [];
    }
  }, [
    reportType,
    quickLink,
    selectedLeaseID,
    fromDate,
    thruDate,
    selectedTag,
    selectedPumperCsv,
    urlToken,
  ]);

  /* -----------------------------------------------------------
     Fetch well-test data
  ------------------------------------------------------------ */
  const fetchWellTestData = useCallback(async () => {
    try {
      const url =
        `${API_BASE}/service_welltests.php?LeaseID=${selectedLeaseID}` +
        `&WellID=${selectedWellID}&FromDate=${fromDate}&ThruDate=${thruDate}`;
      const resp = await fetch(url, {
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      if (!resp.ok) throw new Error("Network response was not ok");
      const json = await resp.json();

      return json.map((i) => {
        const wellsOn =
          i.WellsOn !== undefined && i.WellsOn !== null
            ? parseFloat(i.WellsOn) || 0
            : 0;
        const totalWells =
          i.TotalWells !== undefined && i.TotalWells !== null
            ? parseFloat(i.TotalWells) || 0
            : 0;
        const wellsDown =
          i.WellsDown !== undefined && i.WellsDown !== null
            ? parseFloat(i.WellsDown) || 0
            : Math.max(0, totalWells - wellsOn);

        return {
          GaugeDate: i.GaugeDate,
          /* __ts: Unix-ms timestamp for time-proportional x-axis (well tests are always daily) */
          __ts: gaugeKeyToTs(i.GaugeDate, "CD"),
          TestOil: parseFloat(i.TestOil) || 0,
          TestWater: parseFloat(i.TestWater) || 0,
          TestGas: parseFloat(i.TestGas) || 0,
          Tbg: parseFloat(i.TbgPressure) || 0,
          Csg: parseFloat(i.CsgPressure) || 0,
          JTF: parseFloat(i.JTF) || 0,
          FTF: parseFloat(i.FTF) || 0,
          FAP: parseFloat(i.FAP) || 0,
          GFFAP: parseFloat(i.GFFAP) || 0,
          SND: parseFloat(i.SNDepth) || 0,
          WellsOn: wellsOn,
          TotalWells: totalWells,
          WellsDown: wellsDown,
        };
      });
    } catch (e) {
      console.error("fetchWellTestData:", e);
      return [];
    }
  }, [selectedLeaseID, selectedWellID, fromDate, thruDate, urlToken]);

  /* -----------------------------------------------------------
     On-mount fetches
  ------------------------------------------------------------ */
  useEffect(() => {
    fetchPreferences();
    fetchLeases();
    fetchPumpers();
  }, [fetchPreferences, fetchLeases, fetchPumpers]);

  /* -----------------------------------------------------------
     Fetch data when dependencies change.
     Uses fetchSeqRef so only the latest request writes state —
     a slow prior request will see its seq is stale and bail.
  ------------------------------------------------------------ */
  useEffect(() => {
    const seq = ++fetchSeqRef.current;
    (async () => {
      setIsLoading(true);
      try {
        const result =
          chartView === "production"
            ? await fetchChartData()
            : await fetchWellTestData();

        /* Stale-request guard: if a newer fetch was kicked off while we
           were awaiting, discard this result silently. */
        if (seq !== fetchSeqRef.current) return;

        const patched = (result || []).map((r) => {
          const on = Number.isFinite(parseFloat(r.WellsOn))
            ? parseFloat(r.WellsOn)
            : 0;
          const total = Number.isFinite(parseFloat(r.TotalWells))
            ? parseFloat(r.TotalWells)
            : 0;
          const downRaw = r.WellsDown;
          const down = Number.isFinite(parseFloat(downRaw))
            ? parseFloat(downRaw)
            : Math.max(0, total - on);

          return {
            ...r,
            WellsOn: on,
            TotalWells: total,
            WellsDown: down,
          };
        });

        /* Pad production data to selected date range so x-axis shows
           fromDate→thruDate even when the API returns fewer rows.
           Well-test mode is not padded — its dates are independent of
           reportType and padding with "CM" format would inject wrong keys. */
        const padded =
          chartView === "production"
            ? padDataToDateRange(patched, fromDate, thruDate, reportType)
            : patched;
        setData(padded);
        setIsLoading(false);
      } catch (e) {
        console.error("fetchData:", e);
        if (seq === fetchSeqRef.current) setIsLoading(false);
      }
    })();
    /* No cleanup needed — the seq check handles staleness without
       requiring a mutable mounted flag or AbortController. */
  }, [chartView, fetchChartData, fetchWellTestData, fromDate, thruDate, reportType]);

  /* -----------------------------------------------------------
     Debounced preference saver
  ------------------------------------------------------------ */
  const debouncedSavePreferences = useCallback(
    debounce(async () => {
      if (!userID) {
        console.warn("savePreferences: missing userID; skipping PATCH.");
        return;
      }
      try {
        const headers = { "Content-Type": "application/json" };
        if (urlToken) headers.Authorization = `Bearer ${urlToken}`;

        const resp = await fetch(`${API_BASE}/api/userdetails.php?tokdebug=1`, {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            UserID: userID,
            ChartsPref: {
              chartTypes,
              colors,
              logarithmic,
              disabledSeries,
              trendline,
              showTrendline: trendline.enabled, // legacy compatibility
            },
          }),
        });
        const json = await resp.json();
        if (!json.success) console.error("savePrefs:", json.message);
      } catch (e) {
        console.error("savePrefs:", e);
      }
    }, 300),
    [userID, chartTypes, colors, logarithmic, disabledSeries, trendline, urlToken],
  );

  useEffect(() => {
    debouncedSavePreferences();
  }, [
    chartTypes,
    colors,
    logarithmic,
    disabledSeries,
    trendline,
    debouncedSavePreferences,
  ]);

  /* -----------------------------------------------------------
     QuickLink handler
  ------------------------------------------------------------ */
  const handleQuickLinkChange = (qd) => {
    if (qd === "Custom") {
      setQuickLink("Custom");
      return;
    }
    setQuickLink(qd);
    const { from, thru } = deriveDates(qd);
    setFromDate(from);
    setThruDate(thru);
  };

  const handleFromDateChange = (v) => {
    setFromDate(v);
    setQuickLink("Custom");
  };

  const handleThruDateChange = (v) => {
    setThruDate(v);
    setQuickLink("Custom");
  };

  /* -----------------------------------------------------------
     Remaining handlers
  ------------------------------------------------------------ */
  const handleToggle = (field) =>
    setChartTypes((p) => ({
      ...p,
      [field]: p[field] === "line" ? "bar" : "line",
    }));

  const handleColorChange = (c) => {
    if (!colorPicker.field) return;
    setColors((p) => ({ ...p, [colorPicker.field]: c.hex }));
  };

  const handleTagChange = (value) => {
    setSelectedTag(value);

    if (
      chartView === "production" &&
      value !== "All" &&
      selectedLeaseID !== "~ALL~"
    ) {
      /* Reset Lease and Pumper together — a specific pumper may not cover
         the leases in the new tag, producing a silent empty response. */
      setSelectedLeaseID("~ALL~");
      setSelectedPumperCsv("");
    }
  };

  /* When a specific Lease is chosen, reset Tag and Pumper so the API
     call never receives contradictory filters (e.g. LeaseID=X + Tag=Y
     where lease X doesn't belong to tag Y → silent empty response). */
  const handleLeaseChange = (value) => {
    setSelectedLeaseID(value);
    if (value !== "~ALL~") {
      // LeaseID and (Tag|PumpersCsv) are mutually exclusive: EM's
      // RptEMLeases set is scoped by Tag+Pumper and the final JOIN
      // drops any LeaseID not in that set, yielding a silent empty.
      setSelectedTag("All");
      setSelectedPumperCsv("");
    }
  };

  const handleLegendClick = ({ dataKey }) =>
    setDisabledSeries((p) =>
      p.includes(dataKey) ? p.filter((i) => i !== dataKey) : [...p, dataKey],
    );

  const resetLegend = () => setDisabledSeries([]);

  const setSeriesVisible = (key, isVisible) => {
    setDisabledSeries((prev) => {
      const has = prev.includes(key);
      if (isVisible) {
        return has ? prev.filter((k) => k !== key) : prev;
      }
      return has ? prev : [...prev, key];
    });
  };

  const updateTrendline = (field, value) => {
    setTrendline((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const enableTrendline = () => {
    setTrendline((prev) => {
      const nextEnabled = !prev.enabled;
      if (!nextEnabled) {
        return {
          ...prev,
          enabled: false,
        };
      }
      return {
        ...prev,
        enabled: true,
        startDate: prev.startDate || fromDate,
        endDate: prev.endDate || thruDate,
      };
    });
  };

  const applyCurrentRangeToTrendline = () => {
    setTrendline((prev) => ({
      ...prev,
      startDate: fromDate,
      endDate: thruDate,
    }));
  };

  const clearTrendlineInputs = () => {
    setTrendline((prev) => ({
      ...prev,
      startDate: "",
      startValue: "",
      endDate: "",
      endValue: "",
    }));
  };

  const renderLegendText = (v, entry) => {
    const { color } = entry;

    const isWellCount =
      v === "WellsOn" || v === "WellsDown" || v === "TotalWells";

    let unit = "BBLS";
    if (
      v === "Gas" ||
      v === "TestGas" ||
      v === "JTF" ||
      v === "FTF" ||
      v === "FAP" ||
      v === "GFFAP" ||
      v === "SND"
    )
      unit = "MCF";

    if (v === "Tbg" || v === "Csg") unit = "PSI";
    if (isWellCount) unit = "WELLS";

    return (
      <span style={{ color: disabledSeries.includes(v) ? "#ccc" : color }}>
        {v} ({unit})
      </span>
    );
  };

  useEffect(() => {
    if (selectedLeaseID !== "~ALL~") {
      const lease = leases.find((l) => l.LeaseID === selectedLeaseID);
      if (lease) {
        setWells(lease.Wells);
        setSelectedWellID(lease.Wells.length ? lease.Wells[0].WellID : "");
      }
    } else {
      setWells(
        leases.flatMap((l) =>
          l.Wells.map((w) => ({ ...w, LeaseName: l.LeaseName })),
        ),
      );
      setSelectedWellID("");
    }
  }, [selectedLeaseID, leases]);

  const handleNextLease = () => {
    const availableLeases =
      chartView === "production"
        ? leases
        : leases.filter((lease) => lease.WellType !== "INJ");

    if (!availableLeases.length) return;

    const idx = availableLeases.findIndex((l) => l.LeaseID === selectedLeaseID);
    const next = idx === -1 ? 0 : (idx + 1) % availableLeases.length;
    /* Route through handleLeaseChange so Tag/Pumper reset logic is applied */
    handleLeaseChange(availableLeases[next].LeaseID);
  };

  const handleNextWell = () => {
    if (!wells.length) return;
    const idx = wells.findIndex((w) => w.WellID === selectedWellID);
    const next = idx === -1 ? 0 : (idx + 1) % wells.length;
    setSelectedWellID(wells[next].WellID);
  };

  const trendlineMeta = useMemo(() => {
    const productionOnly = chartView === "production";
    const granularity = getGaugeGranularity(chartView, reportType);

    const startMoment = normalizeTrendInputMoment(
      trendline.startDate,
      chartView,
      reportType,
    );
    const endMoment = normalizeTrendInputMoment(
      trendline.endDate,
      chartView,
      reportType,
    );

    const startValue = parseTrendNumber(trendline.startValue);
    const endValue = parseTrendNumber(trendline.endValue);

    const visibleStart = normalizeTrendInputMoment(
      fromDate,
      chartView,
      reportType,
    );
    const visibleEnd = normalizeTrendInputMoment(
      thruDate,
      chartView,
      reportType,
    );

    let validationMessage = "";

    if (!productionOnly) {
      validationMessage = "Trendline is available only in Production view.";
    } else if (!trendline.startDate) {
      validationMessage = "Start date is required.";
    } else if (!trendline.endDate) {
      validationMessage = "End date is required.";
    } else if (!String(trendline.startValue).trim()) {
      validationMessage = "Start value is required.";
    } else if (!String(trendline.endValue).trim()) {
      validationMessage = "End value is required.";
    } else if (!startMoment.isValid() || !endMoment.isValid()) {
      validationMessage = "Trendline dates must be valid.";
    } else if (!Number.isFinite(startValue) || !Number.isFinite(endValue)) {
      validationMessage = "Trendline values must be valid numbers.";
    } else if (startMoment.isAfter(endMoment, granularity)) {
      validationMessage = "Start date must be on or before end date.";
    }

    const isValid = !validationMessage;
    const overlapsVisibleDomain =
      isValid &&
      visibleStart.isValid() &&
      visibleEnd.isValid() &&
      !endMoment.isBefore(visibleStart, granularity) &&
      !startMoment.isAfter(visibleEnd, granularity);

    const clippedStart =
      overlapsVisibleDomain &&
      startMoment.isBefore(visibleStart, granularity);
    const clippedEnd =
      overlapsVisibleDomain &&
      endMoment.isAfter(visibleEnd, granularity);

    const effectiveStartMoment =
      overlapsVisibleDomain && clippedStart
        ? visibleStart.clone()
        : startMoment.isValid()
          ? startMoment.clone()
          : startMoment;

    const effectiveEndMoment =
      overlapsVisibleDomain && clippedEnd
        ? visibleEnd.clone()
        : endMoment.isValid()
          ? endMoment.clone()
          : endMoment;

    return {
      productionOnly,
      isValid,
      validationMessage,
      overlapsVisibleDomain,
      granularity,
      startMoment,
      endMoment,
      startValue,
      endValue,
      visibleStart,
      visibleEnd,
      clippedStart,
      clippedEnd,
      effectiveStartMoment,
      effectiveEndMoment,
    };
  }, [chartView, reportType, trendline, fromDate, thruDate]);

  /* Custom tooltip that reduces noise in two ways:
     - Suppressed entirely on ghost/padding rows and on columns where every
       visible series value is zero, null, or undefined.
     - Only shows lines for series with non-zero values, so a column with
       one real value doesn't list seven zeros.
     This does NOT do geometric proximity to lines — Recharts triggers the
     tooltip for every x-position that has a data point regardless of cursor
     y-position.  The heuristic just filters out the noisiest cases. */
  const renderTooltipContent = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    /* Filter to entries with meaningful values */
    const meaningful = payload.filter(
      (p) =>
        p.dataKey !== "__manualTrendline" &&
        p.value !== null &&
        p.value !== undefined &&
        p.value !== 0,
    );
    if (!meaningful.length) return null;

    /* BUG-2: label is now a __ts timestamp (number) — format it for display.
       Monthly charts show M/YY, daily/well-test charts show M/D/YY. */
    const formattedLabel =
      typeof label === "number" && label > 0
        ? chartView === "production" && reportType === "CM"
          ? moment(label).format("MM-YYYY")
          : moment(label).format("M/D/YY")
        : label;

    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #d1d5db",
          borderRadius: 4,
          padding: "8px 12px",
          fontSize: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        }}
      >
        <p style={{ margin: "0 0 4px", fontWeight: 600 }}>{formattedLabel}</p>
        {meaningful.map((p) => (
          <p key={p.dataKey} style={{ margin: "2px 0", color: p.color }}>
            {p.name}: {typeof p.value === "number" ? p.value.toFixed(2) : p.value}
          </p>
        ))}
      </div>
    );
  };

  /* BUG-2: With a numeric time-axis, bars have no implicit category width.
     computedBarSize (defined after chartData) provides a dynamic pixel width
     based on data density so bars remain readable at any zoom level. */
  const renderBarSeries = (series) => (
    <Bar
      key={`bar-${series.key}`}
      yAxisId={series.yAxisId}
      dataKey={series.key}
      name={series.name}
      fill={colors[series.key]}
      fillOpacity={0.35}
      stackId={stacked && series.stackId ? series.stackId : undefined}
      barSize={computedBarSize}
    />
  );

  const renderLineSeries = (series) => (
    <Line
      key={`line-${series.key}`}
      yAxisId={series.yAxisId}
      type="monotone"
      dataKey={series.key}
      name={series.name}
      stroke={colors[series.key]}
      dot={false}
      activeDot={{ r: 4 }}
      strokeWidth={3}
      connectNulls={false}
    />
  );

  /* Compute log-scale tick arrays for left + right YAxis when logarithmic
     mode is on.  Covers both major decades and minor intermediate steps so
     CartesianGrid draws fine horizontal rules at each position. */
  const logActiveData = logarithmic ? filterLogData(data) : [];
  const leftAxisKeys = ["Oil", "ProducedWater", "InjectedWater"].filter(
    (k) => !disabledSeries.includes(k),
  );
  const rightAxisKeys = (
    chartView === "production"
      ? ["Gas", "Tbg", "Csg"]
      : ["TestGas", "Tbg", "Csg", "JTF", "FTF", "FAP", "GFFAP", "SND"]
  ).filter((k) => !disabledSeries.includes(k));
  const leftVals = logActiveData
    .flatMap((r) => leftAxisKeys.map((k) => r[k]))
    .filter((v) => v > 0);
  const rightVals = logActiveData
    .flatMap((r) => rightAxisKeys.map((k) => r[k]))
    .filter((v) => v > 0);
  const leftLogTicks =
    logarithmic && leftVals.length
      ? computeLogTicks(Math.min(...leftVals), Math.max(...leftVals))
      : undefined;
  const rightLogTicks =
    logarithmic && rightVals.length
      ? computeLogTicks(Math.min(...rightVals), Math.max(...rightVals))
      : undefined;

  /* Shared log tick label formatter: only label powers of 10 so minor
     grid-line ticks don't clutter the axis with too many numbers. */
  const fmtLogTick = (v) => {
    const isPowerOf10 = v > 0 && Math.abs(Math.log10(v) % 1) < 0.001;
    if (!isPowerOf10) return "";
    return v >= 1e6
      ? `${(v / 1e6).toFixed(0)}M`
      : v >= 1e3
        ? `${(v / 1e3).toFixed(0)}K`
        : `${v}`;
  };
  const fmtLinearTick = (v) =>
    v >= 1e6
      ? `${(v / 1e6).toFixed(1)}M`
      : v >= 1e3
        ? `${(v / 1e3).toFixed(1)}K`
        : v.toFixed(1);

  /* Merge manual trendline values into chart data when enabled.
     Important fix:
     - inject anchor rows at the effective visible start/end points
       when those dates are not present in API data
     - this guarantees the line can render from entered points even
       when the chosen dates do not exactly match returned buckets */
  const chartData = useMemo(() => {
    const base = logarithmic ? filterLogData(data) : data;

    if (
      chartView !== "production" ||
      !trendline.enabled ||
      !trendlineMeta.isValid ||
      !trendlineMeta.overlapsVisibleDomain
    ) {
      return base;
    }

    const totalSpan =
      trendlineMeta.endMoment.valueOf() - trendlineMeta.startMoment.valueOf();

    const valueAtMoment = (targetMoment) => {
      if (!targetMoment?.isValid?.()) return undefined;
      const ratio =
        totalSpan === 0
          ? 0
          : (targetMoment.valueOf() - trendlineMeta.startMoment.valueOf()) /
            totalSpan;

      const rawValue =
        trendlineMeta.startValue +
        (trendlineMeta.endValue - trendlineMeta.startValue) * ratio;

      if (!Number.isFinite(rawValue)) return undefined;
      if (logarithmic && rawValue <= 0) return undefined;
      return rawValue;
    };

    const working = base.map((row) => ({
      ...row,
      __manualTrendline: undefined,
    }));

    const ensureAnchorAtMoment = (anchorMoment) => {
      if (!anchorMoment?.isValid?.()) return;
      const gaugeKey = formatGaugeMoment(anchorMoment, chartView, reportType);
      const ts = anchorMoment.valueOf();
      const existingIdx = working.findIndex((row) => row.GaugeDate === gaugeKey);

      if (existingIdx >= 0) {
        working[existingIdx] = {
          ...working[existingIdx],
          __trendAnchor: true,
          __ts: ts,
        };
      } else {
        working.push(makeTrendAnchorRow(gaugeKey, ts));
      }
    };

    ensureAnchorAtMoment(trendlineMeta.effectiveStartMoment);
    ensureAnchorAtMoment(trendlineMeta.effectiveEndMoment);

    working.sort((a, b) =>
      compareGaugeKeys(a.GaugeDate, b.GaugeDate, chartView, reportType),
    );

    return working.map((row) => {
      const rowMoment = parseGaugeMoment(row.GaugeDate, chartView, reportType);

      if (!rowMoment.isValid()) {
        return {
          ...row,
          __manualTrendline: undefined,
        };
      }

      if (
        row.__ghost &&
        !row.__trendAnchor &&
        (rowMoment.isBefore(
          trendlineMeta.effectiveStartMoment,
          trendlineMeta.granularity,
        ) ||
          rowMoment.isAfter(
            trendlineMeta.effectiveEndMoment,
            trendlineMeta.granularity,
          ))
      ) {
        return {
          ...row,
          __manualTrendline: undefined,
        };
      }

      let trendValue = undefined;

      if (
        !rowMoment.isBefore(
          trendlineMeta.effectiveStartMoment,
          trendlineMeta.granularity,
        ) &&
        !rowMoment.isAfter(
          trendlineMeta.effectiveEndMoment,
          trendlineMeta.granularity,
        )
      ) {
        trendValue = valueAtMoment(rowMoment);
      }

      return {
        ...row,
        __manualTrendline: trendValue,
      };
    });
  }, [data, logarithmic, trendline, trendlineMeta, chartView, reportType]);

  /* BUG-2: Compute time-axis domain from selected From/Thru and a dynamic
     bar width that stays readable regardless of data density.
     effectiveRpt is "CD" for well-test (always daily) or the current
     reportType for production. */
  const effectiveRpt = chartView === "production" ? reportType : "CD";
  const xDomainFrom = dateStrToTs(fromDate, effectiveRpt);
  const xDomainThru = dateStrToTs(thruDate, effectiveRpt);
  const realPointCount = chartData.filter(
    (d) => !d.__ghost && !d.__trendAnchor,
  ).length;
  const computedBarSize = Math.max(
    2,
    Math.min(24, Math.floor(700 / Math.max(1, realPointCount))),
  );

  const trendlineRenderablePointCount = useMemo(
    () =>
      chartData.reduce((count, row) => {
        return row.__manualTrendline !== null &&
          row.__manualTrendline !== undefined &&
          Number.isFinite(row.__manualTrendline)
          ? count + 1
          : count;
      }, 0),
    [chartData],
  );

  const shouldRenderTrendline =
    chartView === "production" &&
    trendline.enabled &&
    trendlineMeta.isValid &&
    trendlineMeta.overlapsVisibleDomain &&
    trendlineRenderablePointCount >= 2;

  const handlePrint = () => {
    const html = chartRef.current.innerHTML;
    const original = document.body.innerHTML;
    const leaseName =
      leases.find((l) => l.LeaseID === selectedLeaseID)?.LeaseName ||
      "All Leases";

    const fmt = (d) => {
      const m = moment(d, "YYYY-MM-DD", true);
      return m.isValid() ? m.format("M/D/YY") : String(d || "");
    };

    const pumperLabel =
      selectedPumperCsv && String(selectedPumperCsv).trim()
        ? pumpers.find((p) => String(p.UserID) === String(selectedPumperCsv))
            ?.FullName || selectedPumperCsv
        : "";

    document.body.innerHTML = `
      <style>
        @media print{
          @page{size:landscape;margin:.5cm;}
          body{margin:0;font-family:Arial}
          .reset-legend-button{display:none !important;}
          .fab-container{display:none !important;}
          .settings-panel{display:none !important;}
          .filters-container{display:none !important;}
          .color-picker-modal{display:none !important;}
          button{display:none !important;}
        }
      </style>
      <h1>${chartView === "production" ? "Production" : "Well Test"} Chart</h1>
      <p><b>Date Range:</b> ${fmt(fromDate)} to ${fmt(thruDate)}</p>
      <p><b>Lease:</b> ${leaseName}</p>
      ${
        chartView === "production" && selectedTag !== "All"
          ? `<p><b>Tag:</b> ${selectedTag}</p>`
          : ""
      }
      ${
        chartView === "production" && pumperLabel
          ? `<p><b>Pumper:</b> ${pumperLabel}</p>`
          : ""
      }
      ${
        chartView === "wellTest"
          ? `<p><b>Well:</b> ${selectedWellID || "All Wells"}</p>`
          : ""
      }
      <p><b>Y-Axis:</b> ${logarithmic ? "Logarithmic" : "Linear"}</p>
      ${
        chartView === "production" &&
        trendline.enabled &&
        trendlineMeta.isValid
          ? `<p><b>Trendline:</b> ${fmt(trendline.startDate)} @ ${trendline.startValue || ""} → ${fmt(trendline.endDate)} @ ${trendline.endValue || ""}</p>`
          : ""
      }
      ${html}
    `;
    window.print();
    document.body.innerHTML = original;
    window.location.reload();
  };

  const handleEditClick = () => {
    setIsSidePanelOpen((o) => !o);
    setIsEditing((o) => !o);
  };

  const handleChartViewChange = () => {
    setChartView((v) => (v === "production" ? "wellTest" : "production"));
    setData([]);
  };

  const trendlineHelperText =
    chartView !== "production"
      ? "Trendline is available only in Production view."
      : trendline.enabled && trendlineMeta.validationMessage
        ? trendlineMeta.validationMessage
        : trendline.enabled &&
            trendlineMeta.isValid &&
            !trendlineMeta.overlapsVisibleDomain
          ? "Trendline falls outside the current visible From/To range."
          : trendline.enabled &&
              trendlineMeta.isValid &&
              (trendlineMeta.clippedStart || trendlineMeta.clippedEnd)
            ? "Trendline is clipped to the current visible From/To range. Expand the date range to view the full projection."
            : logarithmic &&
                trendline.enabled &&
                trendlineMeta.isValid &&
                (trendlineMeta.startValue <= 0 || trendlineMeta.endValue <= 0)
              ? "In logarithmic mode, non-positive trendline values are suppressed."
              : "";

  /* True when data has no real series values (trend anchors / ghost rows
     should not count as real returned data). */
  const hasRealData = data.some((row) =>
    seriesKeys.some((key) => row[key] !== undefined && row[key] !== null),
  );
  const isGhostOnly = !hasRealData;

  return (
    <>
      <div className="chart-container">
        <div className={`filters-container ${isEditing ? "editing" : ""}`}>
          <div className="filters">
            <div className="filter-group">
              <div className="filter-item">
                <label>Time Range</label>
                <select
                  value={quickLink}
                  onChange={(e) => handleQuickLinkChange(e.target.value)}
                >
                  <option value="30">Last 30 days</option>
                  <option value="CM">Current Month</option>
                  <option value="3D">Last 3 Days</option>
                  <option value="7D">Last 7 Days</option>
                  <option value="LM">Last Month</option>
                  <option value="3M">Last 3 months</option>
                  <option value="6M">Last 6 months</option>
                  <option value="CY">Current Year</option>
                  <option value="LY">Last Year</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>

              <div className="filter-item">
                <label>From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => handleFromDateChange(e.target.value)}
                />
              </div>

              <div className="filter-item">
                <label>To</label>
                <input
                  type="date"
                  value={thruDate}
                  onChange={(e) => handleThruDateChange(e.target.value)}
                />
              </div>

              <div className="filter-item">
                <label>Lease</label>
                <div className="select-with-nav">
                  <select
                    value={selectedLeaseID}
                    onChange={(e) => handleLeaseChange(e.target.value)}
                  >
                    {chartView === "production" ? (
                      <option value="~ALL~">All Leases</option>
                    ) : (
                      leases.length > 0 && (
                        <option
                          value={leases[0].LeaseID}
                          key={leases[0].LeaseID}
                        >
                          {leases[0].LeaseName}
                        </option>
                      )
                    )}
                    {leases
                      .filter(
                        (lease) =>
                          chartView === "production" ||
                          lease.WellType !== "INJ",
                      )
                      .map((lease) => (
                        <option key={lease.LeaseID} value={lease.LeaseID}>
                          {lease.LeaseName}
                        </option>
                      ))}
                  </select>
                  <button
                    className="next-well-button"
                    onClick={handleNextLease}
                  >
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </div>

              {chartView === "production" ? (
                <div className="filter-item">
                  <label>Tag</label>
                  <select
                    value={selectedTag}
                    onChange={(e) => handleTagChange(e.target.value)}
                  >
                    {tags.map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="filter-item">
                  <label>Well</label>
                  <div className="select-with-nav">
                    <select
                      value={selectedWellID}
                      onChange={(e) => setSelectedWellID(e.target.value)}
                      disabled={selectedLeaseID === "~ALL~"}
                    >
                      <option value="">Select a well</option>
                      {wells.map((well) => (
                        <option key={well.UniqID} value={well.WellID}>
                          {well.WellID}
                        </option>
                      ))}
                    </select>
                    <button
                      className="next-well-button"
                      onClick={handleNextWell}
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                </div>
              )}

              {chartView === "production" && (
                <div className="filter-item">
                  <label>Pumper</label>
                  <select
                    value={selectedPumperCsv}
                    onChange={(e) => setSelectedPumperCsv(e.target.value)}
                  >
                    <option value="">All Pumpers</option>
                    {pumpers.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.FullName && u.FullName.trim()
                          ? u.FullName
                          : u.UserID}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {chartView === "production" && (
                <div className="filter-item">
                  <label>Report Type</label>
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <option value="CD">Daily</option>
                    <option value="CM">Monthly</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="action-button print-button"
              onClick={handlePrint}
            >
              <FontAwesomeIcon icon={faPrint} className="icon" /> Print
            </button>
            <button
              className="action-button settings-button"
              onClick={handleEditClick}
            >
              <FontAwesomeIcon
                icon={isSidePanelOpen ? faTimes : faCog}
                className="icon"
              />
              {isSidePanelOpen ? "Done" : "Edit"}
            </button>
          </div>
        </div>

        <div ref={chartRef} className="chart-container-inner">
          {isLoading ? (
            <div className="loading-spinner"></div>
          ) : isGhostOnly ? (
            <div className="no-data-message">
              No data available for the selected filters.
              {selectedTag !== "All" || selectedLeaseID !== "~ALL~" ? (
                <span> Try broadening your Lease, Tag, or date range.</span>
              ) : null}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={700} key={chartView}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                {/* BUG-2 FIX: Numeric time-axis so spacing is proportional to
                    actual elapsed time.  domain is pinned to the user's
                    selected From/Thru so empty stretches get correct width.
                    scale="time" uses d3 scaleTime for natural tick placement
                    at month/day boundaries. */}
                <XAxis
                  dataKey="__ts"
                  type="number"
                  scale="time"
                  domain={[xDomainFrom, xDomainThru]}
                  tickFormatter={(ts) =>
                    chartView === "production" && reportType === "CM"
                      ? moment(ts).format("M/YY")
                      : moment(ts).format("M/D/YY")
                  }
                  tick={{ fontSize: 12, angle: -45, textAnchor: "end" }}
                  height={60}
                  padding={{ left: 20, right: 20 }}
                />

                <YAxis
                  yAxisId="left"
                  scale={logarithmic ? "log" : "linear"}
                  domain={logarithmic ? [1, "auto"] : [0, (max) => max * 1.1]}
                  allowDataOverflow
                  label={{ value: "BBLS", angle: -90, position: "insideLeft" }}
                  tickCount={logarithmic ? undefined : 7}
                  ticks={leftLogTicks}
                  tickFormatter={logarithmic ? fmtLogTick : fmtLinearTick}
                />

                <YAxis
                  yAxisId="right"
                  orientation="right"
                  scale={logarithmic ? "log" : "linear"}
                  domain={logarithmic ? [1, "auto"] : [0, (max) => max * 1.1]}
                  allowDataOverflow
                  label={{
                    value: "MCF / PSI",
                    angle: -90,
                    position: "insideRight",
                  }}
                  tickCount={logarithmic ? undefined : 7}
                  ticks={rightLogTicks}
                  tickFormatter={logarithmic ? fmtLogTick : fmtLinearTick}
                />

                <YAxis
                  yAxisId="wells"
                  orientation="right"
                  scale="linear"
                  domain={[0, (max) => max * 1.1]}
                  allowDataOverflow
                  tickCount={7}
                  label={{
                    value: "WELLS",
                    angle: -90,
                    position: "insideRight",
                    offset: 48,
                  }}
                />

                <Tooltip content={renderTooltipContent} />
                <Legend
                  onClick={handleLegendClick}
                  formatter={renderLegendText}
                />

                {visibleBarSeries.map(renderBarSeries)}
                {visibleLineSeries.map(renderLineSeries)}

                {shouldRenderTrendline && (
                  <Line
                    yAxisId="left"
                    type="linear"
                    dataKey="__manualTrendline"
                    name="Trendline"
                    stroke="#111827"
                    strokeDasharray="8 4"
                    strokeWidth={2}
                    dot={false}
                    activeDot={false}
                    connectNulls={false}
                    legendType="none"
                    isAnimationActive={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}

          <button className="reset-legend-button" onClick={resetLegend}>
            <FontAwesomeIcon icon={faRedo} className="icon" /> Reset Legend
          </button>
        </div>

        <div className={`settings-panel ${isSidePanelOpen ? "open" : ""}`}>
          <h2>
            Settings
            {displayNameForSettings ? ` (${displayNameForSettings})` : ""}
          </h2>

          <div className="settings-section">
            <h3>Series Visibility</h3>
            {seriesKeys.map((k) => (
              <label key={k} className="checkbox-label series-checkbox">
                <input
                  type="checkbox"
                  checked={!disabledSeries.includes(k)}
                  onChange={(e) => setSeriesVisible(k, e.target.checked)}
                />
                {k}
              </label>
            ))}
          </div>

          <div className="settings-section">
            <h3>Chart Type and Color</h3>
            {Object.entries(
              chartView === "production"
                ? {
                    Oil: chartTypes.Oil,
                    ProducedWater: chartTypes.ProducedWater,
                    InjectedWater: chartTypes.InjectedWater,
                    Gas: chartTypes.Gas,
                    Tbg: chartTypes.Tbg,
                    Csg: chartTypes.Csg,
                    WellsOn: chartTypes.WellsOn,
                    WellsDown: chartTypes.WellsDown,
                    TotalWells: chartTypes.TotalWells,
                  }
                : {
                    TestOil: chartTypes.TestOil,
                    TestWater: chartTypes.TestWater,
                    TestGas: chartTypes.TestGas,
                    Tbg: chartTypes.Tbg,
                    Csg: chartTypes.Csg,
                    JTF: chartTypes.JTF,
                    FTF: chartTypes.FTF,
                    FAP: chartTypes.FAP,
                    GFFAP: chartTypes.GFFAP,
                    SND: chartTypes.SND,
                    WellsOn: chartTypes.WellsOn,
                    WellsDown: chartTypes.WellsDown,
                    TotalWells: chartTypes.TotalWells,
                  },
            ).map(([field, type]) => (
              <div key={field} className="settings-item">
                <span>{field}</span>
                <div className="chart-controls">
                  <button
                    className="chart-type-button"
                    onClick={() => handleToggle(field)}
                  >
                    {type === "line" ? (
                      <FontAwesomeIcon icon={faChartBar} />
                    ) : (
                      <FontAwesomeIcon icon={faChartLine} />
                    )}
                  </button>
                  <div
                    className="color-box"
                    style={{ backgroundColor: colors[field] }}
                    onClick={() => setColorPicker({ visible: true, field })}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="settings-section">
            <h3>Y-Axis Scale</h3>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={logarithmic}
                onChange={() => setLogarithmic(!logarithmic)}
              />
              Logarithmic
            </label>
          </div>

          <div className="settings-section">
            <h3>Trendline</h3>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={trendline.enabled}
                onChange={enableTrendline}
                disabled={chartView !== "production"}
              />
              Enable custom trendline
            </label>

            <div className="trendline-actions">
              <button
                className="trendline-small-button"
                onClick={applyCurrentRangeToTrendline}
                disabled={!trendline.enabled || chartView !== "production"}
              >
                Use current range
              </button>
              <button
                className="trendline-small-button secondary"
                onClick={clearTrendlineInputs}
                disabled={!trendline.enabled || chartView !== "production"}
              >
                Clear
              </button>
            </div>

            <div className="trendline-grid">
              <label className="trendline-input-group">
                <span>Start Date</span>
                <input
                  type="date"
                  value={trendline.startDate}
                  onChange={(e) => updateTrendline("startDate", e.target.value)}
                  disabled={!trendline.enabled || chartView !== "production"}
                />
              </label>

              <label className="trendline-input-group">
                <span>Start Value</span>
                <input
                  type="number"
                  step="any"
                  value={trendline.startValue}
                  onChange={(e) => updateTrendline("startValue", e.target.value)}
                  disabled={!trendline.enabled || chartView !== "production"}
                />
              </label>

              <label className="trendline-input-group">
                <span>End Date</span>
                <input
                  type="date"
                  value={trendline.endDate}
                  onChange={(e) => updateTrendline("endDate", e.target.value)}
                  disabled={!trendline.enabled || chartView !== "production"}
                />
              </label>

              <label className="trendline-input-group">
                <span>End Value</span>
                <input
                  type="number"
                  step="any"
                  value={trendline.endValue}
                  onChange={(e) => updateTrendline("endValue", e.target.value)}
                  disabled={!trendline.enabled || chartView !== "production"}
                />
              </label>
            </div>

            {trendlineHelperText ? (
              <p
                className={`settings-helper-text ${
                  trendlineMeta.validationMessage && chartView === "production"
                    ? "error"
                    : ""
                }`}
              >
                {trendlineHelperText}
              </p>
            ) : null}
          </div>

          {chartView === "production" && (
            <div className="settings-section">
              <h3>Stacked Options</h3>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={stacked}
                  onChange={() => setStacked(!stacked)}
                />
                Stacked Gas and Tbg
              </label>
            </div>
          )}
        </div>

        {colorPicker.visible && (
          <div
            className="color-picker-modal"
            onClick={() => setColorPicker({ visible: false, field: null })}
          >
            <div
              className="color-picker-container"
              onClick={(e) => e.stopPropagation()}
            >
              <SketchPicker
                color={colors[colorPicker.field]}
                onChange={handleColorChange}
              />
              <button
                className="close-button"
                onClick={() => setColorPicker({ visible: false, field: null })}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <div className="fab-container">
          <button className="fab" onClick={handleChartViewChange}>
            <FontAwesomeIcon icon={faExchangeAlt} />
            {chartView === "production" ? "Wells" : "Prod"}
          </button>
        </div>
      </div>

      <style jsx>{`
        .chart-container {
          --panel-width: 320px;
          min-height: 100vh;
          padding: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: 0px;
          width: 100%;
          box-sizing: border-box;
        }

        .filters-container {
          width: 100%;
          max-width: 100%;
          background: linear-gradient(to right, #ebf4ff, #dee6ff);
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          padding: 8px;
          display: flex;
          align-items: center;
          transition:
            transform 0.3s ease-in-out,
            padding-right 0.3s ease;
          position: sticky;
          top: 0;
          z-index: 1200;
          backdrop-filter: saturate(1.1);
        }

        .filters-container.editing {
          transform: none;
          padding-right: var(--panel-width);
        }

        .filters {
          flex: 1;
          display: flex;
          flex-wrap: nowrap;
          align-items: center;
          min-width: 0;
        }

        .filter-group {
          display: flex;
          flex-wrap: nowrap;
          gap: 10px;
          flex: 1;
          min-width: 0;
        }

        .filter-item {
          display: flex;
          flex-direction: column;
          margin-bottom: 0;
          min-width: 130px;
        }

        .filter-item label {
          margin-bottom: 4px;
          font-size: 11px;
          font-weight: 500;
          color: #4a4a4a;
        }

        .filter-item select,
        .filter-item input {
          width: 100%;
          max-width: 150px;
          padding: 4px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          transition:
            border-color 0.15s ease,
            box-shadow 0.15s ease;
        }

        .filter-item select:focus,
        .filter-item input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
          outline: none;
        }

        .select-with-nav {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .select-with-nav select {
          flex: 1 1 auto;
          min-width: 0;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          margin-left: 16px;
          flex-wrap: nowrap;
          white-space: nowrap;
        }

        .action-button {
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease;
        }

        .print-button {
          background-color: #10b981;
          color: #fff;
          box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
        }

        .print-button:hover {
          background-color: #059669;
        }

        .settings-button {
          background-color: #3b82f6;
          color: #fff;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
        }

        .settings-button:hover {
          background-color: #2563eb;
        }

        .next-lease-button,
        .next-well-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          margin-left: 0;
          background-color: transparent;
          color: #1d72b8;
          border: none;
          cursor: pointer;
          transition: color 0.3s ease;
          flex: 0 0 auto;
        }

        .next-lease-button:hover,
        .next-well-button:hover {
          color: #155a8a;
        }

        .next-lease-button:focus,
        .next-well-button:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(29, 114, 184, 0.4);
        }

        .next-lease-button .fa-icon,
        .next-well-button .fa-icon {
          margin-left: 0;
        }

        .chart-container-inner {
          width: 100%;
          max-width: 100%;
          background-color: #ffffff;
          border-radius: 8px;
          padding: 32px;
          margin-bottom: 32px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: margin-right 0.3s ease;
        }

        .no-data-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 400px;
          font-size: 16px;
          color: #6b7280;
          gap: 8px;
        }

        .loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 400px;
        }

        .loading-spinner::after {
          content: "";
          display: block;
          width: 64px;
          height: 64px;
          border: 8px solid #f3f4f6;
          border-top: 8px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .reset-legend-button {
          margin-top: 16px;
          padding: 8px 16px;
          background-color: #3b82f6;
          color: #fff;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease;
        }

        .reset-legend-button:hover {
          background-color: #2563eb;
        }

        .settings-panel {
          position: fixed;
          top: 160px;
          height: auto;
          max-height: calc(100vh - 136px);
          overflow-y: auto;
          width: var(--panel-width);
          padding: 24px;
          background-color: #ffffff;
          border-left: 1px solid #e5e7eb;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition:
            right 0.3s ease-in-out,
            box-shadow 0.3s ease-in-out;
          right: calc(var(--panel-width) * -1);
          z-index: 1100;
          -webkit-overflow-scrolling: touch;
          touch-action: pan-y;
        }

        .settings-panel.open {
          right: 0;
          box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
        }

        .settings-panel h2 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .settings-section {
          margin-bottom: 24px;
        }

        .settings-section h3 {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .settings-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          gap: 12px;
        }

        .chart-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .chart-type-button {
          padding: 6px;
          border-radius: 4px;
          background-color: #3b82f6;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease;
        }

        .chart-type-button:hover {
          background-color: #2563eb;
        }

        .color-box {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
        }

        .checkbox-label input {
          margin-right: 8px;
        }

        .series-checkbox {
          display: flex;
          align-items: center;
          margin: 6px 0;
          font-size: 13px;
          color: #374151;
          user-select: none;
        }

        .trendline-actions {
          display: flex;
          gap: 8px;
          margin-top: 10px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }

        .trendline-small-button {
          padding: 6px 10px;
          border-radius: 4px;
          background-color: #2563eb;
          color: #fff;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition:
            background-color 0.15s ease,
            opacity 0.15s ease;
        }

        .trendline-small-button:hover {
          background-color: #1d4ed8;
        }

        .trendline-small-button.secondary {
          background-color: #6b7280;
        }

        .trendline-small-button.secondary:hover {
          background-color: #4b5563;
        }

        .trendline-small-button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .trendline-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-top: 10px;
        }

        .trendline-input-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
          font-size: 12px;
          color: #374151;
        }

        .trendline-input-group input {
          width: 100%;
          padding: 4px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .trendline-input-group input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
          outline: none;
        }

        .settings-helper-text {
          margin-top: 10px;
          font-size: 12px;
          line-height: 1.4;
          color: #6b7280;
        }

        .settings-helper-text.error {
          color: #b91c1c;
        }

        .color-picker-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1300;
        }

        .color-picker-container {
          background-color: #ffffff;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .close-button {
          margin-top: 16px;
          padding: 8px 16px;
          background-color: #ef4444;
          color: #fff;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease;
        }

        .close-button:hover {
          background-color: #dc2626;
        }

        .fab-container {
          position: fixed;
          bottom: 32px;
          right: 32px;
          z-index: 1250;
        }

        .fab {
          width: 56px;
          height: 56px;
          border-radius: 28px;
          background-color: #3b82f6;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .fab:hover {
          background-color: #2563eb;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
        }

        .chart-container:has(.settings-panel.open) .chart-container-inner {
          margin-right: var(--panel-width);
        }

        .chart-container:has(.settings-panel.open) .filters-container {
          padding-right: var(--panel-width);
        }

        .settings-panel.open ~ .fab-container {
          right: calc(32px + var(--panel-width));
        }

        @media (max-width: 1024px) {
          .filters {
            flex-wrap: wrap;
          }

          .filter-group {
            flex-wrap: wrap;
            gap: 12px;
          }

          .filter-item {
            min-width: 180px;
          }

          .filter-item select,
          .filter-item input {
            max-width: 200px;
          }

          .settings-panel {
            top: 112px;
            width: 88vw;
            right: -88vw;
            max-height: calc(100vh - 128px);
          }

          .settings-panel.open {
            right: 0;
          }

          .chart-container:has(.settings-panel.open) .chart-container-inner {
            margin-right: 88vw;
          }

          .settings-panel.open ~ .fab-container {
            right: calc(32px + 8vw);
          }

          .filters-container.editing {
            padding-right: 8px;
          }

          .trendline-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 640px) {
          .filters-container {
            padding: 6px;
          }

          .filter-group {
            gap: 8px;
          }

          .filter-item label {
            font-size: 10px;
          }

          .filter-item select,
          .filter-item input {
            max-width: 180px;
          }

          .action-buttons {
            margin-left: 8px;
            gap: 6px;
          }
        }
      `}</style>
    </>
  );
};

export default ChartComponent;